// HSSNA 错误处理:RFC 7807 Problem Details 解析 + 动作路由
// 规格: SPEC §4.4 / H-13 / H-ENG-13 / T1-02
//
// 设计:
//   - parseProblemDetails(Response): 将 fetch 响应解析为 HssnaError
//     * 优先识别 hssna:code (SSOT 错误码)
//     * 无 code 时按 HTTP 状态码回退到该状态下的首个已知错误码
//     * 非 JSON / 解析失败 → 用 HTTP 状态码兜底
//   - handleError(HssnaError): 返回该错误应触发的动作序列
//     * 部分错误码需触发多个动作 (如 403-002 → report-idor + show-modal)
//   - isHssnaError(unknown): 类型守卫

import { ErrorCode, ERROR_DEFINITIONS, getErrorDefinition } from './codes-gen.js';
import type { HssnaError, ProblemDetails } from './types.js';

/**
 * 错误处理动作
 * - retry          : 自动重试 (配合退避策略)
 * - redirect-login : 跳转登录页
 * - show-toast     : 全局 toast 提示
 * - show-modal     : 模态弹窗 (用于需用户确认的严重错误)
 * - report-idor    : 上报 IDOR 安全事件 (NF-SEC-04 / H-SEC-13)
 * - silent         : 静默处理 (如 AI 置信度不足已转人工)
 */
export type ErrorAction =
  | 'retry'
  | 'redirect-login'
  | 'show-toast'
  | 'show-modal'
  | 'report-idor'
  | 'silent';

/** 已知的全部 ErrorCode 字面量集合 (用于 isHssnaError 校验) */
const KNOWN_CODES: ReadonlySet<string> = new Set<string>(Object.values(ErrorCode));

/**
 * 判断给定值是否为已知的 HSSNA ErrorCode 字面量
 */
function isKnownCode(value: unknown): value is ErrorCode {
  return typeof value === 'string' && KNOWN_CODES.has(value);
}

/**
 * 按 HTTP 状态码回退查找 ErrorCode
 * 选取 SSOT 中该状态码下排序最靠前的一个;无匹配则回退到 HSSNA-500-001
 */
function findCodeByStatus(status: number): ErrorCode {
  for (const def of Object.values(ERROR_DEFINITIONS)) {
    if (def.status === status) {
      return def.code;
    }
  }
  return ErrorCode.HSSNA_500_001;
}

/**
 * 类型守卫:判断 unknown 是否为合法 HssnaError
 * 要求:对象 + 四个必填字段齐全 + code 属于 SSOT
 */
export function isHssnaError(error: unknown): error is HssnaError {
  if (typeof error !== 'object' || error === null) {
    return false;
  }
  const e = error as Record<string, unknown>;
  return (
    typeof e.code === 'string' &&
    isKnownCode(e.code) &&
    typeof e.title === 'string' &&
    typeof e.status === 'number' &&
    typeof e.detail === 'string'
  );
}

/**
 * 解析 fetch Response (RFC 7807 Problem Details) 为 HssnaError
 *
 * 解析策略:
 *   1. 尝试 response.json() 读取 ProblemDetails
 *   2. 若 body.hssna:code 在 SSOT 中 → 用该错误码
 *   3. 否则按 HTTP 状态码回退到 SSOT 中同状态的首个错误码
 *   4. title/detail 优先取响应体,缺省回退到 SSOT 定义
 *   5. 提取 instance 与 hssna:trace-id
 */
export async function parseProblemDetails(response: Response): Promise<HssnaError> {
  const httpStatus = response.status;

  let body: ProblemDetails | null = null;
  try {
    const parsed: unknown = await response.json();
    if (parsed !== null && typeof parsed === 'object') {
      body = parsed as ProblemDetails;
    }
  } catch {
    // 非 JSON 响应 (如 502 网关 HTML),body 保持 null,走状态码回退
    body = null;
  }

  const rawCode = body?.['hssna:code'];
  const code: ErrorCode = isKnownCode(rawCode) ? rawCode : findCodeByStatus(httpStatus);
  const def = getErrorDefinition(code);

  return {
    code,
    title: body?.title ?? def.title,
    status: body?.status ?? def.status,
    detail: body?.detail ?? def.detail,
    instance: body?.instance,
    traceId: body?.['hssna:trace-id'],
  };
}

/**
 * 错误码 → 动作路由表
 *
 * 返回 ErrorAction[] 是因为部分错误需触发多个动作
 * (如 403-002 家庭隔离越权 → 上报 IDOR + 弹窗告知用户)。
 *
 * 路由规则 (SPEC §4.4.4):
 *   - 401                       → redirect-login
 *   - 403-002 (家庭隔离越权)    → report-idor + show-modal
 *   - 403-001 (禁止访问)        → show-toast  (其他 403 同此,如 403-201/701/1201)
 *   - 409-003 (重复幂等键)      → show-toast
 *   - 429                       → retry + show-toast
 *   - 5xx                       → retry
 *   - 其他 (含 200-502 AI 低置信)→ show-toast
 *
 * 注: HSSNA-200-502 (AI 置信度不足, HTTP 200) 属业务正常响应,
 *     走 "其他" 分支提示用户,不做重试或登录跳转。
 */
export function handleError(error: HssnaError): ErrorAction[] {
  // 1. 家庭隔离越权 (IDOR) — 安全事件上报 + 模态弹窗
  if (error.code === ErrorCode.HSSNA_403_002) {
    return ['report-idor', 'show-modal'];
  }

  // 2. 重复 Idempotency-Key — 提示用户请求已处理
  if (error.code === ErrorCode.HSSNA_409_003) {
    return ['show-toast'];
  }

  // 3. 401 未认证 — 跳转登录
  if (error.status === 401) {
    return ['redirect-login'];
  }

  // 4. 429 限流 — 自动重试 + 提示
  if (error.status === 429) {
    return ['retry', 'show-toast'];
  }

  // 5. 5xx 服务端错误 — 自动重试 + toast 提示 (避免用户对故障无感知)
  if (error.status >= 500) {
    return ['retry', 'show-toast'];
  }

  // 6. 兜底 (403-001 禁止访问 / 404 / 422 / 200-502 等) — toast 提示
  return ['show-toast'];
}
